// SAD-498 (SOL-02): typed solution items. An item is a source-backed need, an
// inferred need, an internal assumption or an unknown. A draft source stays
// draft, and a quantity left blank is shown as Unknown — never as zero.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Choice, options, TextField } from '../PastPerformance/Fields';
export const ITEM_KINDS = [
    'source_backed_need',
    'inferred_need',
    'internal_assumption',
    'unknown'
];
const blank = {
    id: '',
    kind: 'source_backed_need',
    text: '',
    sourceLabel: '',
    sourceDate: '',
    sourceStatus: 'draft',
    confidence: '',
    validationStatus: 'unvalidated',
    quantity: '',
    editBasis: ''
};
export const itemKindLabel = (kind)=>kind.replace(/_/g, ' ');
export function ItemRow({ item }) {
    return /*#__PURE__*/ _jsx("li", {
        tabIndex: -1,
        "data-target-id": item.id,
        "data-target-field": "text",
        "data-testid": Testids.captureSolutionReview.affected,
        className: "space-y-1 rounded-md border p-2",
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.captureSolution.itemRow,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-2 text-sm",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: itemKindLabel(item.kind)
                        }),
                        item.kind === 'source_backed_need' && /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: item.sourceStatus === 'final' ? 'Final source' : 'Draft source — not final bid authority'
                        }),
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: item.validationStatus
                        }),
                        item.confidence && /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: `${item.confidence} confidence`
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: item.text
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "text-xs",
                    children: [
                        "Quantity: ",
                        item.quantity || 'Unknown',
                        item.sourceLabel && ` · Source: ${item.sourceLabel}`,
                        item.sourceDate && ` (${item.sourceDate})`
                    ]
                }),
                item.editBasis && /*#__PURE__*/ _jsxs("div", {
                    className: "text-xs",
                    children: [
                        "Basis: ",
                        item.editBasis
                    ]
                })
            ]
        })
    });
}
export function ItemsReadOnly({ items }) {
    return /*#__PURE__*/ _jsx("ul", {
        className: "space-y-2",
        children: items.map((i)=>/*#__PURE__*/ _jsx(ItemRow, {
                item: i
            }, i.id))
    });
}
export function ItemsEditor({ items, priorIds, onChange, approachAccepted, onAcceptedChange }) {
    const [draft, setDraft] = useState(blank);
    const patch = (p)=>setDraft((d)=>({
                ...d,
                ...p
            }));
    const editing = draft.id !== '' && priorIds.has(draft.id);
    const isSource = draft.kind === 'source_backed_need';
    const valid = draft.text.trim() !== '' && (!isSource || draft.sourceLabel.trim() !== '') && (!editing || draft.editBasis.trim() !== '');
    function commit() {
        const next = {
            ...draft,
            id: draft.id || crypto.randomUUID(),
            sourceStatus: isSource ? draft.sourceStatus : '',
            quantity: draft.kind === 'unknown' ? '' : draft.quantity.trim()
        };
        onChange(draft.id ? items.map((i)=>i.id === draft.id ? next : i) : [
            ...items,
            next
        ]);
        setDraft(blank);
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Typed items"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Sort what the solution rests on: a need a source states, a need the team inferred, an internal assumption, or an unknown. Accepting the approach never makes a draft source final."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-2",
                children: items.map((i)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("ul", {
                                children: /*#__PURE__*/ _jsx(ItemRow, {
                                    item: i
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.captureSolution.itemEdit,
                                onClick: ()=>setDraft({
                                        ...i,
                                        editBasis: ''
                                    }),
                                children: "Edit item"
                            })
                        ]
                    }, i.id))
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Item type",
                testId: Testids.captureSolution.itemKind,
                value: draft.kind,
                options: options(ITEM_KINDS),
                onChange: (kind)=>patch({
                        kind
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Item",
                multiline: true,
                testId: Testids.captureSolution.itemText,
                value: draft.text,
                onChange: (text)=>patch({
                        text
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Source",
                testId: Testids.captureSolution.itemSourceLabel,
                value: draft.sourceLabel,
                onChange: (sourceLabel)=>patch({
                        sourceLabel
                    })
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Source date (YYYY-MM-DD)",
                testId: Testids.captureSolution.itemSourceDate,
                value: draft.sourceDate,
                onChange: (sourceDate)=>patch({
                        sourceDate
                    })
            }),
            isSource && /*#__PURE__*/ _jsx(Choice, {
                label: "Source status",
                testId: Testids.captureSolution.itemSourceStatus,
                value: draft.sourceStatus,
                options: options([
                    'draft',
                    'final'
                ]),
                onChange: (sourceStatus)=>patch({
                        sourceStatus
                    })
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Confidence",
                testId: Testids.captureSolution.itemConfidence,
                value: draft.confidence,
                options: [
                    {
                        value: '',
                        label: 'not assessed'
                    },
                    ...options([
                        'high',
                        'medium',
                        'low'
                    ])
                ],
                onChange: (confidence)=>patch({
                        confidence
                    })
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Validation",
                testId: Testids.captureSolution.itemValidation,
                value: draft.validationStatus,
                options: options([
                    'unvalidated',
                    'validated',
                    'invalidated'
                ]),
                onChange: (validationStatus)=>patch({
                        validationStatus
                    })
            }),
            draft.kind !== 'unknown' && /*#__PURE__*/ _jsx(TextField, {
                label: "Quantity (leave blank if not stated)",
                testId: Testids.captureSolution.itemQuantity,
                value: draft.quantity,
                onChange: (quantity)=>patch({
                        quantity
                    })
            }),
            editing && /*#__PURE__*/ _jsx(TextField, {
                label: "Basis for this edit",
                required: true,
                testId: Testids.captureSolution.itemBasis,
                value: draft.editBasis,
                onChange: (editBasis)=>patch({
                        editBasis
                    })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.itemSave,
                disabled: !valid,
                onClick: commit,
                children: editing ? 'Update item' : 'Add item'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.approachAccepted,
                "aria-pressed": approachAccepted,
                onClick: ()=>onAcceptedChange(!approachAccepted),
                children: approachAccepted ? 'Internal approach accepted' : 'Accept internal approach'
            })
        ]
    });
}
